import { useEffect, useState } from "react";
import {
    useDeleteShortLinkMutation,
    useShortLinksQuery,
    useUpdateShortLinkMutation,
} from "@/hooks/ReactQuery/ShortLinkReactQuery/ShortLinkReactQuery";
import { FgChannelLinks } from "./fragments/FgChannelLinks";
import { FgCreateForm } from "./fragments/FgCreateForm";
import { FgGroupTabs } from "./fragments/FgGroups";
import { FgShortLinkItem } from "./fragments/FgShortLinkItem";
import { MdQrCode } from "./fragments/MdQrCode";
import { MdShortLinkEdit } from "./fragments/MdShortLinkEdit";
import { MdShortLinkStats } from "./fragments/MdShortLinkStats";

export function FdShortLink(props) {
    // undefined: 전체, null: 그룹 없음, 문자열: 그 그룹
    const [groupFilter, setGroupFilter] = useState(undefined);
    const allQuery = useShortLinksQuery();
    const shortLinksQuery = useShortLinksQuery({ group: groupFilter });
    const updateShortLinkMutation = useUpdateShortLinkMutation();
    const deleteShortLinkMutation = useDeleteShortLinkMutation();

    const [qrTarget, setQrTarget] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const [statsTarget, setStatsTarget] = useState(null);

    const pages = shortLinksQuery?.data?.pages;
    const items = pages?.flatMap(page => page.items) || [];
    const total = pages?.[0]?.total || 0;
    const allItems = allQuery?.data?.pages?.flatMap(page => page.items) || [];
    const groups = allQuery?.data?.pages?.[0]?.groups || [];
    const ungrouped = allQuery?.data?.pages?.[0]?.ungrouped || 0;

    // 보던 그룹이 없어지면(마지막 링크를 옮김·삭제) 전체로
    const groupMissing = (typeof groupFilter === 'string' && !groups.some(g => g.name === groupFilter)) || (groupFilter === null && !ungrouped);
    useEffect(() => {
        if (groupMissing && !allQuery.isFetching) {
            setGroupFilter(undefined);
        }
    }, [groupMissing, allQuery.isFetching]);

    const handleToggleOpenInApp = (item) => {
        updateShortLinkMutation.mutate({ id: item.id, body: { openInApp: !item.openInApp } });
    }

    const handleDelete = (item) => {
        if (!window.confirm('삭제하면 이 단축 URL 로 더 이상 이동할 수 없고 클릭 기록도 사라집니다. 삭제할까요?')) {
            return;
        }

        deleteShortLinkMutation.mutate({ id: item.id });
    }

    return (
        <div className="flex flex-col gap-5">
            <FgChannelLinks items={allItems} isLoading={allQuery?.isLoading} onOpenQr={setQrTarget} onOpenStats={setStatsTarget} />

            <FgCreateForm onOpenQr={setQrTarget} groups={groups} defaultGroup={typeof groupFilter === 'string' ? groupFilter : ''} />

            <section className="rounded-[15px] border border-gray-100 shadow-[var(--defaultBoxShadow)] bg-white p-5 min-[993px]:p-7 flex flex-col">
                <div className="flex items-baseline justify-between gap-2">
                    <h2 className="text-xl font-bold">내 단축 URL <span className="text-base font-semibold text-[var(--mainColor)]">{total}</span></h2>
                    <p className="text-xs text-gray-400">최대 1000개</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">같은 링크를 채널별로 따로 만들면 어디서 많이 눌렀는지 비교할 수 있어요.</p>

                <FgGroupTabs groups={groups} ungrouped={ungrouped} value={groupFilter} onChange={setGroupFilter} />

                {shortLinksQuery?.isLoading &&
                    <ul className="mt-2 flex flex-col divide-y" aria-busy="true">
                        {[0, 1, 2].map(i => (
                            <li key={i} className="py-4 flex gap-4 animate-pulse">
                                <div className="w-10 h-10 rounded-full bg-gray-100" />
                                <div className="flex-1 flex flex-col gap-2">
                                    <div className="h-3 w-24 rounded bg-gray-100" />
                                    <div className="h-4 w-48 rounded bg-gray-100" />
                                    <div className="h-3 w-full rounded bg-gray-100" />
                                </div>
                            </li>
                        ))}
                    </ul>
                }

                {!shortLinksQuery?.isLoading && items.length === 0 &&
                    <div className="py-12 flex flex-col items-center gap-3 text-center">
                        <div className="w-14 h-14 rounded-full bg-[var(--mainColor100)] flex items-center justify-center">
                            <img src="/assets/icons/link_default_mainColor.svg" alt="" className="w-7 h-7" />
                        </div>
                        <p className="text-sm font-semibold">{groupFilter === undefined ? '아직 만든 단축 URL 이 없어요' : '이 그룹에 링크가 없어요'}</p>
                        <p className="text-xs text-gray-500">위에서 {groupFilter === undefined ? '첫 ' : ''}링크를 줄여 보세요.</p>
                    </div>
                }

                {items.length > 0 &&
                    <ul className="mt-2 flex flex-col divide-y divide-gray-100">
                        {items.map(item => (
                            <FgShortLinkItem
                                key={item.id}
                                item={item}
                                onToggleOpenInApp={handleToggleOpenInApp}
                                onEdit={setEditTarget}
                                onOpenStats={setStatsTarget}
                                onDelete={handleDelete}
                                onOpenQr={setQrTarget}
                                onSelectGroup={setGroupFilter}
                            />
                        ))}
                    </ul>
                }

                {shortLinksQuery?.hasNextPage &&
                    <button
                        type="button"
                        disabled={shortLinksQuery.isFetchingNextPage}
                        onClick={() => shortLinksQuery.fetchNextPage()}
                        className="mt-3 self-center rounded-full border border-gray-300 px-6 py-2 text-sm font-semibold hover:bg-gray-50"
                    >
                        {shortLinksQuery.isFetchingNextPage ? '불러오는 중...' : '더 보기'}
                    </button>
                }
            </section>

            <MdQrCode shortLink={qrTarget} onClose={() => setQrTarget(null)} />
            <MdShortLinkEdit shortLink={editTarget} groups={groups} onClose={() => setEditTarget(null)} />
            <MdShortLinkStats shortLink={statsTarget} onClose={() => setStatsTarget(null)} />
        </div>
    );
}
